import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Workflow } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  useWorkflowExecutions,
  useActiveExecutions,
  useExecutionMetrics,
  useCancelExecution,
  useWorkflowExecutionsRealtime,
  useSimulateWorkflow,
  useExecutionDurationTrend,
} from "@/hooks/workflows";
import { useWorkflows } from "@/hooks/workflows/useWorkflows";

import {
  ActiveExecutionsCard,
  ExecutionMetricsCard,
  ExecutionList,
  ExecutionDetailSheet,
  ExecutionFiltersBar,
  StartWorkflowButton,
  ExecutionDurationChart,
} from "@/components/workflows";

import { WorkflowExecution, ExecutionFilters } from "@/types/workflow";

const WorkflowExecutions = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ExecutionFilters>({});
  const [selectedExecution, setSelectedExecution] = useState<WorkflowExecution | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  // Enable realtime updates
  useWorkflowExecutionsRealtime();

  // Fetch data
  const { data: executions, isLoading: executionsLoading } = useWorkflowExecutions(filters);
  const { data: activeExecutions, isLoading: activeLoading } = useActiveExecutions();
  const { data: metrics, isLoading: metricsLoading } = useExecutionMetrics();
  const { data: workflows } = useWorkflows();
  const { data: durationTrend, isLoading: trendLoading } = useExecutionDurationTrend(
    filters.workflow_id,
    24
  );

  const cancelMutation = useCancelExecution();
  const simulateMutation = useSimulateWorkflow();

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["workflow-executions"] });
    queryClient.invalidateQueries({ queryKey: ["active-executions"] });
    queryClient.invalidateQueries({ queryKey: ["execution-metrics"] });
    queryClient.invalidateQueries({ queryKey: ["execution-duration-trend"] });
    toast.success("Data vernieuwd");
  };

  const handleViewExecution = (execution: WorkflowExecution) => {
    setSelectedExecution(execution);
    setDetailSheetOpen(true);
  };

  const handleCancelExecution = async (executionId: string) => {
    try {
      await cancelMutation.mutateAsync(executionId);
      toast.success("Execution geannuleerd");
    } catch (error) {
      toast.error("Kon execution niet annuleren");
    }
  };

  const handleStartWorkflow = async (workflowId: string) => {
    try {
      toast.info("Workflow wordt gestart...");
      await simulateMutation.mutateAsync(workflowId);
      toast.success("Workflow simulatie voltooid");
    } catch (error) {
      console.error("Error starting workflow:", error);
      toast.error("Kon workflow niet starten");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Workflow className="h-7 w-7" />
            Workflow Executions
          </h1>
          <p className="text-muted-foreground">
            Monitor en beheer workflow uitvoeringen in realtime
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StartWorkflowButton
            workflows={workflows || []}
            onStartWorkflow={handleStartWorkflow}
            isLoading={simulateMutation.isPending}
          />
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Vernieuwen
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid gap-6 md:grid-cols-2">
        <ActiveExecutionsCard 
          executions={activeExecutions || []} 
          isLoading={activeLoading} 
        />
        <ExecutionMetricsCard 
          metrics={metrics} 
          isLoading={metricsLoading} 
        />
      </div>

      {/* Duration Trend Chart */}
      <ExecutionDurationChart
        data={durationTrend || []}
        isLoading={trendLoading}
        title={filters.workflow_id 
          ? `Duur Trend - ${workflows?.find(w => w.id === filters.workflow_id)?.name || 'Workflow'}`
          : "Duur Trend (alle workflows, 24u)"
        }
      />

      {/* Execution History */}
      <Card>
        <CardHeader>
          <CardTitle>Uitvoeringsgeschiedenis</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ExecutionFiltersBar
            filters={filters}
            onFiltersChange={setFilters}
            workflows={workflows || []}
          />
          <ExecutionList
            executions={executions || []}
            isLoading={executionsLoading}
            onViewExecution={handleViewExecution}
            onCancelExecution={handleCancelExecution}
            isCancelling={cancelMutation.isPending}
          />
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <ExecutionDetailSheet
        execution={selectedExecution}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />
    </div>
  );
};

export default WorkflowExecutions;
